/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { FormattedNumber } from 'react-intl';
import { Condition } from '../../../../../common/src/domain/defect/condition/conditionTableSchema';
import { InspectionFinding } from '../../../../../common/src/domain/inspectionFinding/inspectionFindingTableSchema';
import { SurveyElementGroup } from '../../../../../common/src/domain/surveyElementGroup/surveyElementGroupTableSchema';
import { ElementGroupNames } from '../../../../../common/src/enums/ElementGroupNames';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchGet } from '../../../store/fetch';
import { getConstructionTypeText } from '../../../utils/data/elementConstructionTypeList';
import { getAssetThumbnailUrl } from '../../../utils/getAssetThumbnailUrl';
import * as htmlHelper from '../../../utils/htmlHelper';
import { PanelBody, PanelButtonLink, PanelHeading, TableExpandButton } from '../../elements';
import { CraRiskScoreDot } from '../../utils/CraRiskScoreDot';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';
import { IElement } from '../Interfaces/IElement';
import { IUnit } from '../Interfaces/IUnit';
import { CraCondition } from './CraCondition';

// eslint-disable-next-line no-shadow
enum CraPhases {
    Initial = 'initial',
    History = 'history',
    Inspection = 'inspection',
}

type SurveyElementGroupView = Pick<
    SurveyElementGroup,
    'craMaxHistoryScore' | 'craMaxInitialScore' | 'craMaxInspectionScore' | 'name' | 'initialRemarks' | 'inspectionRemarks'
>;

interface ISurveyElementGroupData {
    [ElementGroupNames.Superstructure]: SurveyElementGroupView;
    [ElementGroupNames.Substructure]: SurveyElementGroupView;
    [ElementGroupNames.Foundation]: SurveyElementGroupView;
}

type BreakDownOpenState = { [key: string]: boolean };

async function hasUnitInformation(surveyId: string, unit: IUnit) {
    const condition = (await fetchGet(`/api/surveys/${surveyId}/units/${unit.id}/condition`)).json;
    if (condition.id) {
        return true;
    }

    const inspectionFindings = (await fetchGet(`/api/surveys/${surveyId}/inspectionFindings`, { unitId: unit.id })).json.items;
    if (inspectionFindings.length > 0) {
        return true;
    }

    return false;
}

export function ConditionReportCra(props: { objectId: string; surveyId: string; isAssetDescriptionVisible: boolean; elements: IElement[] }) {
    const translate = useTranslate();

    const [surveyElementGroupData, setSurveyElementGroupData] = React.useState<ISurveyElementGroupData>();
    const [breakDownOpenState, setBreakDownOpenState] = React.useState<BreakDownOpenState>({});
    const [elementsToHide, setElementsToHide] = React.useState<string[]>([]);
    const [unitsToHide, setUnitsToHide] = React.useState<string[]>([]);

    React.useEffect(() => {
        const fetchData = async () => {
            for (const element of props.elements) {
                if (element.isArchived) {
                    let hasUnitWithInformation = false;
                    for (const unit of element.units) {
                        if (await hasUnitInformation(props.surveyId, unit)) {
                            hasUnitWithInformation = true;
                            break;
                        }
                    }
                    if (!hasUnitWithInformation) {
                        setElementsToHide((oldValue) => [...oldValue, element.id]);
                    }
                }
                for (const unit of element.units) {
                    if (unit.isArchived) {
                        if (!(await hasUnitInformation(props.surveyId, unit))) {
                            setUnitsToHide((oldValue) => [...oldValue, unit.id]);
                        }
                    }
                }
            }
            const data = {
                [ElementGroupNames.Superstructure]: (
                    await fetchGet(`/api/surveys/${props.surveyId}/elementGroups/${ElementGroupNames.Superstructure}`)
                ).json,
                [ElementGroupNames.Substructure]: (await fetchGet(`/api/surveys/${props.surveyId}/elementGroups/${ElementGroupNames.Substructure}`))
                    .json,
                [ElementGroupNames.Foundation]: (await fetchGet(`/api/surveys/${props.surveyId}/elementGroups/${ElementGroupNames.Foundation}`)).json,
            };
            setSurveyElementGroupData(data);
        };
        void fetchData();
    }, [props.elements, props.surveyId]);

    function expandAll() {
        const expandedBreakDownOpenState = {} as { [key: string]: boolean };
        expandedBreakDownOpenState[ElementGroupNames.Superstructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Substructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Foundation] = true;
        for (const element of props.elements) {
            expandedBreakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                expandedBreakDownOpenState[unit.id] = true;
                expandedBreakDownOpenState[unit.id + CraPhases.Initial] = true;
                expandedBreakDownOpenState[unit.id + CraPhases.History] = true;
                expandedBreakDownOpenState[unit.id + CraPhases.Inspection] = true;
                for (const manifestation of unit.manifestations) {
                    expandedBreakDownOpenState[manifestation.id] = true;
                }
            }
        }
        setBreakDownOpenState(expandedBreakDownOpenState);
    }
    function collapseAll() {
        setBreakDownOpenState({});
    }

    function onChangeBreakdownOpenState(key: string) {
        breakDownOpenState[key] = !breakDownOpenState[key];

        setBreakDownOpenState({ ...breakDownOpenState });
    }

    if (surveyElementGroupData === undefined) {
        return (
            <table className="table">
                <thead key="spinner">
                    <tr>
                        <th className="text-center">
                            <img src="/assets/img/spinner-amsterdam.svg" alt="loading" />
                        </th>
                    </tr>
                </thead>
            </table>
        );
    }

    return (
        <div className="panel" id="CRAConditionAssessment">
            <PanelHeading text={`${translate('cra.cra')} ${translate('survey.conditionReport')}`} icon="fa fa-tasks" />
            <PanelBody whiteBackground={true}>
                <PanelButtonLink
                    onClick={() => expandAll()}
                    text={translate('survey.expandAllComposition')}
                    icon="fa fa-plus-square-o"
                    xsmall={true}
                    rightAllign={true}
                />
                <PanelButtonLink
                    onClick={() => collapseAll()}
                    text={translate('survey.collapseAllComposition')}
                    icon="fa fa-minus-square-o "
                    xsmall={true}
                    rightAllign={true}
                />
                <table className="table">
                    <thead className="mt10">
                        <tr className="dark">
                            <th style={{ width: '50px' }}>{translate('common.code')}</th>
                            <th>{translate('common.part')}</th>
                            <th>{translate('common.material')}</th>
                            <th>{translate('common.year')}</th>
                            <th className="text-right" style={{ width: '40px' }}>
                                {translate('cra.initial')}
                            </th>
                            <th className="text-right" style={{ width: '40px' }}>
                                {translate('common.history')}
                            </th>
                            <th className="text-right" style={{ width: '40px' }}>
                                {translate('cra.inspection')}
                            </th>
                            <th>&nbsp; </th>
                        </tr>
                    </thead>
                    {[
                        surveyElementGroupData[ElementGroupNames.Superstructure],
                        surveyElementGroupData[ElementGroupNames.Substructure],
                        surveyElementGroupData[ElementGroupNames.Foundation],
                    ].map((elementGroup) => (
                        <React.Fragment key={elementGroup.name}>
                            <thead key={elementGroup.name}>
                                <tr style={{ backgroundColor: '#bed2dc' }}>
                                    <th></th>
                                    <th>{translate(`elementGroupName.${elementGroup.name}`)}</th>
                                    <th>&nbsp;</th>
                                    <th>&nbsp;</th>
                                    <th className="text-center">
                                        <CraRiskScoreDot value={elementGroup.craMaxInitialScore} />
                                    </th>
                                    <th className="text-center">
                                        <CraRiskScoreDot value={elementGroup.craMaxHistoryScore} />
                                    </th>
                                    <th className="text-center">
                                        <CraRiskScoreDot value={elementGroup.craMaxInspectionScore} />
                                    </th>
                                    <th className="text-right">
                                        {props.elements.filter((i) => i.elementGroupName === elementGroup.name).length > 0 && (
                                            <TableExpandButton
                                                open={breakDownOpenState[elementGroup.name]}
                                                onClick={() => onChangeBreakdownOpenState(elementGroup.name)}
                                            />
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            {breakDownOpenState[elementGroup.name] && (
                                <>
                                    {elementGroup.initialRemarks && (
                                        <tbody className="br-n">
                                            <tr>
                                                <td></td>
                                                <td colSpan={7}>
                                                    <div className="p10 br2 br-a br-greyer" style={{ backgroundColor: '#d9e5ec' }}>
                                                        <strong>{translate(`cra.${CraPhases.Initial}`)}</strong>
                                                        <br />
                                                        {htmlHelper.newLineToBreak(elementGroup.initialRemarks)}
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    )}

                                    {elementGroup.inspectionRemarks && (
                                        <tbody className="br-n">
                                            <tr>
                                                <td></td>
                                                <td colSpan={7}>
                                                    <div className="p10 br2 br-a br-greyer" style={{ backgroundColor: '#d9e5ec' }}>
                                                        <strong>{translate(`cra.${CraPhases.Inspection}`)}</strong>
                                                        <br />
                                                        {htmlHelper.newLineToBreak(elementGroup.inspectionRemarks)}
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    )}
                                    {props.elements
                                        .filter((i) => i.elementGroupName === elementGroup.name)
                                        .map(
                                            (element) =>
                                                !elementsToHide.includes(element.id) && (
                                                    <React.Fragment key={element.id}>
                                                        <thead className="object-table-element" key={element.id}>
                                                            <tr className="primary">
                                                                <th>{element.code}</th>
                                                                <th>{element.name}</th>
                                                                <th>
                                                                    {element.constructionType && getConstructionTypeText(element.constructionType)}
                                                                </th>
                                                                <th>{element.constructionYear}</th>
                                                                <th colSpan={3}>&nbsp;</th>
                                                                <th className="text-right">
                                                                    {element.units && element.units.length > 0 && (
                                                                        <TableExpandButton
                                                                            open={breakDownOpenState[element.id]}
                                                                            onClick={() => onChangeBreakdownOpenState(element.id)}
                                                                        />
                                                                    )}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody
                                                            className={breakDownOpenState[element.id] ? 'object-table-unit' : 'hidden'}
                                                            key={`${element.id}tbody`}
                                                        >
                                                            {element.units.map(
                                                                (unit) =>
                                                                    !unitsToHide.includes(unit.id) && (
                                                                        <ReportUnitRow
                                                                            key={unit.id}
                                                                            unit={unit}
                                                                            breakDownOpenState={breakDownOpenState}
                                                                            onChangeBreakdownOpenState={(key) => onChangeBreakdownOpenState(key)}
                                                                            surveyId={props.surveyId}
                                                                            objectId={props.objectId}
                                                                            isAssetDescriptionVisible={props.isAssetDescriptionVisible}
                                                                        />
                                                                    ),
                                                            )}
                                                        </tbody>
                                                    </React.Fragment>
                                                ),
                                        )}
                                </>
                            )}
                        </React.Fragment>
                    ))}
                </table>
            </PanelBody>
        </div>
    );
}

type ConditionView = Pick<
    Condition,
    | 'craInitialScore'
    | 'craHistoryScore'
    | 'craInspectionScore'
    | 'craHistoryRemarks'
    | 'craInitialRemarks'
    | 'craInspectionRemarks'
    | 'craInitialUnityCheck'
    | 'craHistoryUnityCheck'
>;
type InspectionFindingView = Pick<InspectionFinding, 'description' | 'id'>;

interface IReportUnitRowData {
    condition: ConditionView;
    inspectionFindings: InspectionFindingView[];
}

function ReportUnitRow(props: {
    unit: IUnit;
    surveyId: string;
    objectId: string;
    breakDownOpenState: BreakDownOpenState;
    isAssetDescriptionVisible: boolean;
    onChangeBreakdownOpenState(name: string): void;
}) {
    const translate = useTranslate();

    const [data, setData] = React.useState<IReportUnitRowData | undefined>();

    React.useEffect(() => {
        const fetchData = async () => {
            const newData = {
                condition: (await fetchGet(`/api/surveys/${props.surveyId}/units/${props.unit.id}/condition`)).json,
                inspectionFindings: (await fetchGet(`/api/surveys/${props.surveyId}/inspectionFindings`, { unitId: props.unit.id })).json.items,
            };
            setData(newData);
        };
        void fetchData();
    }, [props.surveyId, props.unit.id]);

    if (data === undefined) {
        return null;
    }

    return (
        <>
            <tr key={props.unit.id} className="bg-light darker">
                <td>{props.unit.code}</td>
                <td>{props.unit.name}</td>
                <td>{props.unit.material}</td>
                <td>{props.unit.constructionYear}</td>
                <td className="text-center" style={{ width: '30px' }}>
                    <CraRiskScoreDot value={data.condition.craInitialScore} />
                </td>
                <td className="text-center" style={{ width: '30px' }}>
                    <CraRiskScoreDot value={data.condition.craHistoryScore} />
                </td>
                <td className="text-center" style={{ width: '30px' }}>
                    <CraRiskScoreDot value={data.condition.craInspectionScore} />
                </td>
                <td className="text-right">
                    <TableExpandButton
                        open={props.breakDownOpenState[props.unit.id]}
                        onClick={() => props.onChangeBreakdownOpenState(props.unit.id)}
                    />
                </td>
            </tr>
            {props.breakDownOpenState[props.unit.id] && (
                <>
                    <tr>
                        <td></td>
                        <td>
                            <strong>{translate(`cra.${CraPhases.Initial}`)}</strong>
                        </td>
                        <td>
                            {data.condition.craInitialUnityCheck && (
                                <>
                                    Unit checky: <FormattedNumber value={data.condition.craInitialUnityCheck} />
                                </>
                            )}
                        </td>
                        <td colSpan={4} className="text-right">
                            <CraCondition conditionScore={data.condition.craInitialScore} />
                        </td>
                        {data.condition.craInitialRemarks && (
                            <td className="text-right">
                                <TableExpandButton
                                    open={props.breakDownOpenState[props.unit.id + CraPhases.Initial]}
                                    onClick={() => props.onChangeBreakdownOpenState(props.unit.id + CraPhases.Initial)}
                                />
                            </td>
                        )}
                    </tr>
                    {props.breakDownOpenState[props.unit.id + CraPhases.Initial] && data.condition.craInitialRemarks && (
                        <tr>
                            <td></td>
                            <td colSpan={8}>
                                <div className="bg-light darker p10 br2 br-a br-greyer">
                                    {htmlHelper.newLineToBreak(data.condition.craInitialRemarks)}
                                </div>
                            </td>
                        </tr>
                    )}

                    <tr>
                        <td>&nbsp;</td>
                        <td>
                            <strong>{translate(`cra.${CraPhases.History}`)}</strong>
                        </td>
                        <td>
                            {data.condition.craHistoryUnityCheck && (
                                <>
                                    Unity check: <FormattedNumber value={data.condition.craHistoryUnityCheck} />
                                </>
                            )}
                        </td>
                        <td colSpan={4} className="text-right">
                            <CraCondition conditionScore={data.condition.craHistoryScore} />
                        </td>
                        {data.condition.craHistoryRemarks && (
                            <td className="text-right">
                                <TableExpandButton
                                    open={props.breakDownOpenState[props.unit.id + CraPhases.History]}
                                    onClick={() => props.onChangeBreakdownOpenState(props.unit.id + CraPhases.History)}
                                />
                            </td>
                        )}
                    </tr>
                    {props.breakDownOpenState[props.unit.id + CraPhases.History] && data.condition.craHistoryRemarks && (
                        <tr>
                            <td></td>
                            <td colSpan={8}>
                                <div className="bg-light darker p10 br2 br-a br-greyer">
                                    {htmlHelper.newLineToBreak(data.condition.craHistoryRemarks)}
                                </div>
                            </td>
                        </tr>
                    )}

                    <tr>
                        <td>&nbsp;</td>
                        <td>
                            <strong>{translate(`cra.${CraPhases.Inspection}`)}</strong>
                        </td>
                        <td>&nbsp;</td>
                        <td colSpan={4} className="text-right">
                            <CraCondition conditionScore={data.condition.craInspectionScore} />
                        </td>
                        {(data.condition.craInspectionRemarks ||
                            (props.unit.assets && props.unit.assets.length > 0) ||
                            (data.inspectionFindings && data.inspectionFindings.length > 0)) && (
                            <td className="text-right">
                                <TableExpandButton
                                    open={props.breakDownOpenState[props.unit.id + CraPhases.Inspection]}
                                    onClick={() => props.onChangeBreakdownOpenState(props.unit.id + CraPhases.Inspection)}
                                />
                            </td>
                        )}
                    </tr>
                    {props.breakDownOpenState[props.unit.id + CraPhases.Inspection] &&
                        (data.condition.craInspectionRemarks ||
                            (props.unit.assets && props.unit.assets.length > 0) ||
                            (data.inspectionFindings && data.inspectionFindings.length > 0)) && (
                            <tr>
                                <td></td>
                                <td colSpan={8}>
                                    <div className="bg-light darker p10 br2 br-a br-greyer">
                                        {/* Remark */}
                                        {data.condition.craInspectionRemarks && (
                                            <p>{htmlHelper.newLineToBreak(data.condition.craInspectionRemarks)}</p>
                                        )}
                                        {/* Findings */}
                                        {data.inspectionFindings && data.inspectionFindings.length > 0 && (
                                            <div className="row p10 mr20">
                                                <h5> {translate('failureMode.findings')}</h5>
                                                <table className="table bg-light lighter table-condensed table-bordered">
                                                    <tbody>
                                                        {data.inspectionFindings.map((inspectionFinding) => (
                                                            <tr key={inspectionFinding.id}>
                                                                <td>{inspectionFinding.description} </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                        {/* assets */}
                                        <div className="row">
                                            {props.unit.assets &&
                                                props.unit.assets.map((asset) => (
                                                    <div className="col-sm-6 col-md-3" key={asset.id}>
                                                        <div className="thumbnail">
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    showAssetInNewWindow(asset);
                                                                }}
                                                                className={`${asset.type}-report`}
                                                            >
                                                                <img
                                                                    loading="lazy"
                                                                    src={getAssetThumbnailUrl(asset)}
                                                                    height="120px"
                                                                    title={asset.name}
                                                                />
                                                            </a>
                                                            {props.isAssetDescriptionVisible && (
                                                                <div
                                                                    className="caption"
                                                                    title={asset.description ?? ''}
                                                                    style={{
                                                                        minHeight: '80px',
                                                                        maxHeight: '80px',
                                                                        overflow: 'hidden',
                                                                        overflowX: 'hidden',
                                                                    }}
                                                                >
                                                                    {asset.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        )}
                </>
            )}
        </>
    );
}
